import { UploadOutlined } from '@ant-design/icons'
import {
  AutoComplete,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Spin,
  Upload,
} from 'antd'
import { UploadFile } from 'antd/lib'
import dayjs from 'dayjs'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RcFile } from 'antd/es/upload'

import { useBlocktrainRules, useFileTypes, useRailcarRules } from '@hooks'

import UploadFileList from '../../../components/UploadFileList'
import { useCreateRailcar } from '../../../hooks/railcar/useCreateRailcar'
import { BlocktrainDetail, RailcarBase, ScrapFile, SupplierPurchaseOrder } from '../../../types'
import idEntitySendSetter from '../../../utils/id-entity-send-setter'
import parseStringToNumber from '../../../utils/parse-string-to-number'

interface RailcarModalProps {
  visible: boolean
  onCancel: () => void
  blockTrain: BlocktrainDetail
  purchaseOrders?: SupplierPurchaseOrder[]
  materialsOptions?: {
    label: string
    value: number
  }[]
}

interface DropdownOption {
  value: number
  label: string
}

const AddRailcarModal: FC<RailcarModalProps> = ({
  visible,
  onCancel,
  blockTrain,
  purchaseOrders,
  materialsOptions,
}) => {
  const { t } = useTranslation()
  const blocktrainRules = useBlocktrainRules()
  const railCarRules = useRailcarRules()

  const [form] = Form.useForm()
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState<any[]>([])
  const [selectedMaterial, setSelectedMaterial] = useState<number | null>(null)
  const [fileList, setFileList] = useState<ScrapFile[]>()

  const [isCheckboxActive, setIsCheckboxActive] = useState(false)
  const [dropdownOptions, setRailcarTargetOptions] = useState<DropdownOption[]>()

  const handleCheckboxChange = async (checked: boolean) => {
    setIsCheckboxActive(checked)

    if (checked) {
      try {
        const uniqueCombinations = new Set()
        const dropdownOptionsArray = blockTrain.railCars
          .filter(item => {
            const combinationKey = `${blockTrain.id}-${item.railCarNumber}`

            if (uniqueCombinations.has(combinationKey)) {
              return false
            }

            uniqueCombinations.add(combinationKey)
            return true
          })
          .map(item => ({
            value: item.id,
            label: `${blockTrain.id}-${item.railCarNumber} ${item.purchaseOrder?.ExirosRef ?? ''} ${
              item.deliveryNote
            }`,
          }))
        setRailcarTargetOptions(dropdownOptionsArray)
      } catch (error) {
        console.error(error)
        notification.error({
          message: 'Error fetching dropdown data',
        })
      }
    } else {
      setRailcarTargetOptions(undefined)
    }
  }

  const { railCarFileTypes, railCarFileTypesIsLoading } = useFileTypes()

  const resetForm = (close?: boolean) => {
    form.resetFields()
    setFileList(undefined)
    if (close) {
      onCancel()
    }
  }
  const { createRailcar, createRailcarIsFetching } = useCreateRailcar({ resetForm })

  const handleUploadChange = (info: any) => {
    const allowedTypes = ['.pdf', '.jpg', '.png', '.jpeg']
    const validatedFileList = info.fileList
      .map((file: UploadFile) => {
        if (!file.originFileObj || !(file.originFileObj instanceof File)) return null

        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`
        if (!allowedTypes.includes(fileExtension)) {
          notification.error({
            message: t('error_invalid_format'),
            description: t('message_error_document', {
              formats: allowedTypes.join(', '),
            }),
          })
          return null
        }
        return file
      })
      .filter(Boolean)

    setFileList(prevFileList => {
      const prevFiles = prevFileList || []
      const newFiles = validatedFileList.filter(
        (file: { uid: string }) => !prevFiles.some(existingFile => existingFile.uid === file.uid),
      )

      return [...prevFiles, ...newFiles]
    })
  }

  const handleCreateRailcar = async () => {
    await form
      .validateFields()
      .then(values => {
        const payload = idEntitySendSetter(values, ['material'])

        const formData = new FormData()

        Object.entries(payload).forEach(([key, value]) => {
          if (typeof value === 'string' || value instanceof Blob) {
            formData.append(key, value)
          }
        })

        const defaultFileData: {
          copiedFiles: number[]
          files: RcFile[]
          types: string[]
        } = { copiedFiles: [], files: [], types: [] }

        const fileManagment =
          fileList?.reduce((acc, { isCopied, originFileObj, fileType, id }) => {
            if (!isCopied && originFileObj && fileType) {
              acc.files.push(originFileObj)
              acc.types.push(fileType)
            }
            if (!!isCopied) {
              acc.copiedFiles.push(id)
            }
            return acc
          }, defaultFileData) ?? defaultFileData

        const { copiedFiles, files, types } = fileManagment

        createRailcar({
          payload: {
            ...payload,
            ...(!!copiedFiles.length && { copiedFiles }),
            blockTrain: { id: blockTrain.id },
          } as RailcarBase,
          ...(!!files.length && !!types.length && { files, types }),
        })
      })
      .catch(e => {
        console.error(e)
        notification.destroy('validationError')
        notification.error({
          message: t('review_entries'),
          key: 'validationError',
        })
      })
  }

  const handleCancel = () => {
    resetForm(true)
  }

  const handleMaterialChange = (materialId: number) => {
    setSelectedMaterial(materialId)

    const filteredPOs = purchaseOrders?.filter(po => po.material.id === materialId) || []
    setFilteredPurchaseOrders(filteredPOs)

    form.setFieldsValue({
      purchaseOrder: undefined,
    })
  }

  const handleRailcarTargetSelection = (value: number) => {
    const railcarSelected = blockTrain.railCars.find(el => el.id === value)
    if (!railcarSelected) return

    form.setFieldsValue({
      purchaseOrder: railcarSelected.purchaseOrder?.id ?? null,
      material: railcarSelected.material.id,
      deliveryNote: railcarSelected.deliveryNote,
      deliveryNoteDate: dayjs(railcarSelected.deliveryNoteDate),
    })
    setFileList(prevFileList => [
      ...railcarSelected.files.map(file => ({
        uid: `${file.file.uid ?? file.file.id}-copy`,
        name: file.file.fileName || 'Copied File',
        type: file.type.name,
        fileType: file.type.name,
        file: file.file,
        createAt: file.createAt,
        updateAt: file.updateAt,
        deleteAt: file.deleteAt,
        id: file.id,
        isCopied: true,
      })),
      ...(prevFileList?.filter(({ isCopied }) => !isCopied) || []),
    ])
  }

  const rulesInputTn = [
    {
      required: true,
      message: t('please_input_value_in_kg'),
    },
    {
      validator: (_: any, value: string) => {
        const parsedValue = parseStringToNumber(value)
        if (parsedValue <= 0) {
          return Promise.reject(t('input_format_required'))
        }
        return Promise.resolve()
      },
    },
  ]

  const formatNumberWithThousandsSeparator = (value: string) => {
    const numericValue = value.replace(/\D/g, '')
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.') // Add thousands separator
  }

  const handleNumberChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatNumberWithThousandsSeparator(e.target.value)
    form.setFieldsValue({ [field]: formattedValue })
  }

  return (
    <Modal
      title={t('new_railcar')}
      open={visible}
      onCancel={handleCancel}
      cancelText={t('cancel')}
      width={830}
      className="entity-modal railcar-modal"
      okText={t('save')}
      onOk={() => form.submit()}
      okButtonProps={{ disabled: createRailcarIsFetching }}
    >
      <Spin spinning={!!createRailcarIsFetching}>
        <Form form={form} layout="horizontal" onFinish={handleCreateRailcar}>
          <Row gutter={16}>
            <Col span={12}>
              {blockTrain.railCars.length > 0 && (
                <Form.Item label={t('copy_railcar')} name="copy_railcar">
                  <Checkbox
                    onChange={e => handleCheckboxChange(e.target.checked)}
                    checked={isCheckboxActive}
                  ></Checkbox>
                </Form.Item>
              )}
              {isCheckboxActive && (
                <>
                  <Form.Item
                    label={t('copy_railcar_dropdown')}
                    name="dynamicDropdown"
                    rules={[{ required: true, message: t('globals.obligatory_field') }]}
                    help={false}
                  >
                    <Select
                      placeholder={t('globals.placeholder_select')}
                      options={dropdownOptions}
                      showSearch
                      filterOption={(input, option) => {
                        if (!option) return false
                        return option.label.toLowerCase().includes(input.toLowerCase())
                      }}
                      onSelect={handleRailcarTargetSelection}
                    />
                  </Form.Item>
                </>
              )}

              <Form.Item
                label={t('material')}
                name="material"
                rules={[{ required: true, message: t('please_input_material') }]}
                help={false}
                validateTrigger={['']}
              >
                <Select
                  placeholder={t('select_the_material')}
                  options={materialsOptions}
                  onChange={handleMaterialChange}
                  showSearch
                  filterOption={(input, option) => {
                    if (!option) return false
                    return option.label.toLowerCase().includes(input.toLowerCase())
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t('po_number')}
                name="purchaseOrder"
                rules={[
                  {
                    required: !!blockTrain.supplier.sapId && !!filteredPurchaseOrders?.length,
                    message: t('please_input_PO_number'),
                  },
                ]}
                help={false}
                validateTrigger={['']}
              >
                <Select
                  placeholder={t('select_po_#')}
                  options={filteredPurchaseOrders?.map(po => ({
                    value: po.id,
                    label: `${po.PurchaseDocumentNumber} - ${po.material.nameShort}`,
                  }))}
                  disabled={!selectedMaterial || !blockTrain.supplier.sapId}
                  showSearch
                  filterOption={(input, option) => {
                    if (!option) return false
                    return option.label.toLowerCase().includes(input.toLowerCase())
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t('railcar_ID')}
                name="railCarId"
                rules={[
                  ...railCarRules.railCarId,
                  {
                    validator: async (_, value) => {
                      const existingRailcar = blockTrain.railCars.some(
                        // eslint-disable-next-line @typescript-eslint/no-shadow
                        railcar => railcar.railCarId === value,
                      )
                      if (existingRailcar) {
                        return Promise.reject(new Error(t('railcar_id_already_in_use')))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
                help={false}
                validateTrigger={['']}
              >
                <Input placeholder={t('select_the_ID_of_the_railcar')} />
              </Form.Item>
              <Form.Item
                label={t('delivery_note')}
                name="deliveryNote"
                rules={blocktrainRules.deliveryNote}
                help={false}
                validateTrigger={['']}
              >
                <AutoComplete
                  showSearch
                  placeholder={t('select_delivery_note')}
                  filterOption
                  onChange={value => form.setFieldsValue({ deliveryNote: value })}
                  onBlur={() => {
                    const currentValue = form.getFieldValue('deliveryNote')
                    if (currentValue && typeof currentValue === 'number') {
                      form.setFieldsValue({ deliveryNote: currentValue })
                    }
                  }}
                  options={[
                    ...new Set(blockTrain.railCars.map(({ deliveryNote }) => deliveryNote)),
                  ].map(value => ({ value }))}
                />
              </Form.Item>
              <Form.Item
                label={t('delivery_note_date')}
                name="deliveryNoteDate"
                rules={[{ required: true, message: t('please_input_delivery_note_date') }]}
                help={false}
                validateTrigger={['']}
                labelCol={{ span: t('delivery_note_date').length > 24 ? 24 : undefined }}
              >
                <DatePicker placeholder={t('select_date')} />
              </Form.Item>
              <Form.Item
                label={t('gross_kg')}
                name="grossTn"
                rules={rulesInputTn}
                help={false}
                validateTrigger={['']}
              >
                <Input
                  placeholder={t('input_gross_kg')}
                  onChange={e => handleNumberChange('grossTn', e)}
                />
              </Form.Item>

              <Form.Item
                label={t('tare_kg')}
                name="tareTn"
                rules={rulesInputTn}
                help={false}
                validateTrigger={['']}
              >
                <Input
                  placeholder={t('input_tare_kg')}
                  onChange={e => handleNumberChange('tareTn', e)}
                />
              </Form.Item>

              <Form.Item
                label={t('net_kg')}
                name="netTn"
                rules={rulesInputTn}
                help={false}
                validateTrigger={['']}
              >
                <Input
                  placeholder={t('input_net_kg')}
                  onChange={e => handleNumberChange('netTn', e)}
                />
              </Form.Item>
            </Col>
            <div>
              <Divider
                type="vertical"
                style={{ height: '100%', width: '1px', backgroundColor: 'grey' }}
              />
            </div>
            <Col
              span={11}
              style={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '45rem',
                overflowY: 'hidden',
              }}
            >
              <Upload
                onChange={handleUploadChange}
                beforeUpload={() => false}
                fileList={fileList}
                accept=".pdf,.jpg,.png,.jpeg"
                style={{ paddingLeft: '4.5rem' }}
              >
                <Button icon={<UploadOutlined />} className="UploadOutlined">
                  {t('upload_file')}
                </Button>
              </Upload>
              <div className="message-file">{t('message_document')}</div>
              <UploadFileList
                className="upload-file-list"
                fileList={fileList}
                setFileList={setFileList}
                description={item =>
                  item?.isCopied ? (
                    <span>{item.fileType}</span>
                  ) : (
                    <Form.Item
                      key={item?.uid}
                      style={{
                        marginLeft: '0.25rem',
                        marginBottom: item?.isCopied ? 'unset' : undefined,
                      }}
                      label={t('file_type')}
                      className={`clear-styles ${item?.isCopied ? 'copied-file' : ''}`}
                      name={item?.uid}
                      rules={
                        item?.isCopied
                          ? []
                          : [{ required: true, message: t('please_input_file_type') }]
                      }
                      help={false}
                    >
                      <Select
                        size="small"
                        placeholder={t('select')}
                        className="button-select-type"
                        loading={railCarFileTypesIsLoading}
                        onChange={value => {
                          setFileList(prevFiles => {
                            const safeFiles = prevFiles?.map(file =>
                              file.uid === item?.uid ? { ...file, fileType: value } : file,
                            )
                            return safeFiles
                          })
                        }}
                        options={railCarFileTypes?.map(fileType => ({
                          value: fileType.id,
                          label: fileType.name,
                        }))}
                      />
                    </Form.Item>
                  )
                }
              />
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  )
}

export default AddRailcarModal
